import React, { useEffect, useState } from 'react';
import classes from "./WinningsPage2.module.css";
import { FaArrowLeft, FaTrophy, FaMedal, FaUsers, FaArrowRight } from 'react-icons/fa6';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import moment from 'moment-timezone';
import { winningsApi } from '../../http/http';
import SmallLoader from '../../component/SmallLoader';
import { removeUserDetails } from '../../store/slices/userSlice';
import Header2 from '../../component/NewComponents/Header2';
import { useTranslation } from 'react-i18next';

/* ── safe JSON parse ── */
const safeParse = (raw, fb = null) => {
    try { return raw ? JSON.parse(raw) : fb; }
    catch { return fb; }
};

/* ── rank medal / label ── */
const getRankDisplay = (rank) => {
    if (rank === 1) return { emoji: "🥇", label: "1st", gold: true };
    if (rank === 2) return { emoji: "🥈", label: "2nd", gold: false };
    if (rank === 3) return { emoji: "🥉", label: "3rd", gold: false };
    return { emoji: null, label: `#${rank}`, gold: false };
};

/* ════════════════════════════════════
   COMPONENT
════════════════════════════════════ */
const WinningsPage2 = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { userDetails } = useSelector(s => s?.persisted?.userSlice);

    const [winnings, setWinnings] = useState([]);
    const [loading, setLoading] = useState(true);

    const {t}=useTranslation();

    /* ── fetch ── */
    const fetchWinnings = async () => {
        try {
            setLoading(true);
            const r = await winningsApi(userDetails?.token);
            setWinnings(r?.data ?? []);
        } catch (e) {
            if (e?.response?.status === 403) {
                dispatch(removeUserDetails());
                toast.error(t("71"));
                navigate('/');
            } else {
                toast.error(e?.response?.data?.message || t("2"));
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchWinnings(); }, []);

    /* ── total points across all matches ── */
    const totalPoints = winnings.reduce((sum, w) => sum + (Number(w.total_points) || 0), 0);

    /* ── loading ── */
    // if (loading) return (
    //     <div className={classes.page}>
    //         <div className={classes.loaderScreen}><SmallLoader /></div>
    //     </div>
    // );

    return (
        <>
            <Header2 active={3} />
            <div className={classes.page}>
                <div className={classes.bgGlow} />

                {/* ══ CONTENT ══ */}
                <div className={classes.content}>

                    {loading &&
                        <div className={classes.center_loader}>
                            <SmallLoader />
                        </div>
                    }

                    {!loading && winnings.length === 0 ? (
                        <div className={classes.emptyState}>
                            <FaTrophy className={classes.emptyIcon} />
                            <p className={classes.emptyTitle}>{t("190")}</p>
                            <p className={classes.emptyText}>
                                {t("191")}
                            </p>
                        </div>
                    ) : (
                        <div className={classes.list}>
                            {winnings.map((w, i) => {
                                const matchDetails = safeParse(w.match_details);
                                const homeGoals = matchDetails?.goals?.home ?? null;
                                const awayGoals = matchDetails?.goals?.away ?? null;
                                const rank = getRankDisplay(w.user_rank);

                                return (
                                    <div
                                        key={w.match_fixture_id ?? i}
                                        className={`${classes.card} ${rank.gold ? classes.cardGold : ""}`}
                                        style={{ animationDelay: `${i * 0.06}s` }}
                                    
                                    >
                                        {/* ── Rank badge ── */}
                                        <div className={`${classes.rankBadge} ${rank.gold ? classes.rankBadgeGold : ""}`}>
                                            {rank.emoji
                                                ? <span className={classes.rankEmoji}>{rank.emoji}</span>
                                                : <span className={classes.rankNum}>{rank.label}</span>
                                            }
                                        </div>

                                        {/* ── Card body ── */}
                                        <div className={classes.cardBody}>

                                            {/* League + date */}
                                            <div className={classes.cardMeta}>
                                                {w.league_image && (
                                                    <img src={w.league_image} alt={w.league_name} className={classes.leagueLogo} />
                                                )}
                                                <span className={classes.leagueName}>{w.league_name ?? "—"}</span>
                                                <span className={classes.matchDate}>
                                                    {moment.utc(w.match_date_time).local().format("DD MMM YYYY")}
                                                </span>
                                            </div>

                                            {/* Teams + score */}
                                            <div className={classes.matchRow}>
                                                {/* Home */}
                                                <div className={classes.teamBlock}>
                                                    <div className={classes.teamLogoWrap}>
                                                        <img src={w.home_team_image} alt={w.home_team_name} className={classes.teamLogo} />
                                                    </div>
                                                    <span className={classes.teamName}>{w.home_team_name}</span>
                                                </div>

                                                {/* Score */}
                                                <div className={classes.scoreBlock}>
                                                    {homeGoals !== null && awayGoals !== null ? (
                                                        <span className={classes.score}>
                                                            {homeGoals} <span className={classes.scoreDash}>—</span> {awayGoals}
                                                        </span>
                                                    ) : (
                                                        <span className={classes.scoreFt}>{t("192")}</span>
                                                    )}
                                                </div>

                                                {/* Away */}
                                                <div className={`${classes.teamBlock} ${classes.teamBlockRight}`}>
                                                    <div className={classes.teamLogoWrap}>
                                                        <img src={w.away_team_image} alt={w.away_team_name} className={classes.teamLogo} />
                                                    </div>
                                                    <span className={classes.teamName}>{w.away_team_name}</span>
                                                </div>
                                            </div>

                                            {/* Stats row */}
                                            <div className={classes.statsRow}>
                                                {/* Points */}
                                                <div className={classes.statItem}>
                                                    <span className={classes.statNum}>{Number(w.total_points).toLocaleString()}</span>
                                                    <span className={classes.statLabel}>{t("193")}</span>
                                                </div>

                                                <div className={classes.statDivider} />

                                                {/* Users joined */}
                                                <div className={classes.statItem}>
                                                    <span className={classes.statNum}>{w.users_joined?.toLocaleString() ?? "—"}</span>
                                                    <span className={classes.statLabel}>{t("194")}</span>
                                                </div>

                                                <div className={classes.statDivider} />

                                                {/* Rewarded */}
                                                <div className={classes.statItem}>
                                                    <span className={`${classes.rewardedChip} ${w.rewarded ? classes.rewardedYes : classes.rewardedNo}`}>
                                                        {w.rewarded ? t("195") : t("196")}
                                                    </span>
                                                    <span className={classes.statLabel}>{t("197")}</span>
                                                </div>
                                            </div>

                                            {/* Message if available */}
                                            {w.message && (
                                                <div className={classes.messageStrip}>
                                                    <span>{w.message}</span>
                                                </div>
                                            )}

                                            <div className={classes.cardFooter}>
                                                <button
                                                    type="button"
                                                    className={classes.ctaBtn}
                                                    onClick={() => navigate("/match-winnings", {
                                                        state: { match_fixture_id: w?.match_fixture_id }
                                                    })}
                                                >
                                                    <span className={classes.ctaBtnGlow} />
                                                    <span className={classes.ctaBtnText}>
                                                        {t("198")}
                                                    </span>
                                                    <FaArrowRight className={classes.ctaBtnArrow} />
                                                </button>
                                            </div>

                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                </div>
            </div>
        </>

    );
};

export default WinningsPage2;